/**
 * card-qty-toggle.js
 * Custom element: replaces the "Add to Cart" button on product cards with
 * an inline quantity selector after the first add — matching the bulk-order
 * selector UX. Fully synced with the cart drawer / cart notification.
 *
 * Behaviour:
 *  - Click "Add to Cart"  → add 1 to cart, open drawer/notification, show qty selector
 *  - Click "+"            → increase qty by 1, update drawer
 *  - Click "−" (qty > 1) → decrease qty by 1, update drawer
 *  - Click "−" (qty = 1) → remove from cart (qty → 0), show "Add to Cart" button
 */

if (!customElements.get('card-qty-toggle')) {
  customElements.define(
    'card-qty-toggle',
    class CardQtyToggle extends HTMLElement {
      constructor() {
        super();
        this._qty = 0;
        this._busy = false;
      }

      connectedCallback() {
        this.variantId = this.dataset.variantId;
        this.addBtn = this.querySelector('.card-qty-toggle__add-btn');
        this.qtySelector = this.querySelector('.card-qty-toggle__selector');
        this.qtyDisplay = this.querySelector('.card-qty-toggle__count');
        this.minusBtn = this.querySelector('.card-qty-toggle__minus');
        this.plusBtn = this.querySelector('.card-qty-toggle__plus');

        // The cart element (drawer or notification)
        this.cartEl =
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-notification');

        // Bind events
        this.addBtn?.addEventListener('click', this._onAddToCart.bind(this));
        this.minusBtn?.addEventListener('click', this._onMinus.bind(this));
        this.plusBtn?.addEventListener('click', this._onPlus.bind(this));

        // Sync from current cart state on load
        this._syncFromCart();

        // Re-sync when the DOM cart:updated event fires (our own updates)
        this._boundOnCartUpdated = this._onCartUpdated.bind(this);
        document.addEventListener('cart:updated', this._boundOnCartUpdated);

        // Re-sync when Shopify pubsub fires (cart drawer +/- / remove button)
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this._pubsubUnsubscribe = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this._onPubSubCartUpdate.bind(this)
          );
        }
      }

      disconnectedCallback() {
        if (this._boundOnCartUpdated) {
          document.removeEventListener('cart:updated', this._boundOnCartUpdated);
        }
        if (this._pubsubUnsubscribe) {
          this._pubsubUnsubscribe();
        }
      }

      // ─── Cart state sync ─────────────────────────────────────────────────────

      async _syncFromCart() {
        try {
          const cart = await fetch('/cart.js').then((r) => r.json());
          const item = (cart.items || []).find(
            (i) => String(i.variant_id) === String(this.variantId)
          );
          this._qty = item ? item.quantity : 0;
          this._render();
        } catch {
          this._qty = 0;
          this._render();
        }
      }

      _onCartUpdated(e) {
        // Don't re-sync if we fired this event ourselves (prevents loops)
        if (e.detail && e.detail._source === 'card-qty-toggle-' + this.variantId) return;
        this._syncFromCart();
      }

      /**
       * Handles PUB_SUB_EVENTS.cartUpdate from the cart drawer's own updates
       * (qty changes, removes). The payload already contains the full cart state,
       * so we read directly from it instead of doing another fetch.
       */
      _onPubSubCartUpdate(event) {
        // Ignore updates we published ourselves
        if (event.source === 'card-qty-toggle') return;

        // If the payload carries full cart data, use it directly (no extra fetch)
        if (event.cartData && event.cartData.items) {
          const item = event.cartData.items.find(
            (i) => String(i.variant_id) === String(this.variantId)
          );
          const newQty = item ? item.quantity : 0;
          if (newQty !== this._qty) {
            this._qty = newQty;
            this._render();
          }
          return;
        }

        // Fallback: re-fetch cart
        this._syncFromCart();
      }

      // ─── Cart mutations ──────────────────────────────────────────────────────

      /**
       * Add 1 to the cart for the first time.
       * Uses the sections-aware endpoint so the cart drawer renders and opens.
       */
      async _addToCart() {
        if (this._busy) return;
        this._busy = true;
        this._setLoading(true);

        try {
          const sections = this.cartEl
            ? this.cartEl.getSectionsToRender().map((s) => s.id)
            : [];

          const body = JSON.stringify({
            id: this.variantId,
            quantity: 1,
            sections,
            sections_url: window.location.pathname,
          });

          const response = await fetch(`${routes.cart_add_url}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body,
          }).then((r) => r.json());

          if (response.status) {
            // Error (sold out, etc.) — log and bail
            console.error('[card-qty-toggle] add error:', response.description);
            return;
          }

          // Update local state
          this._qty = 1;
          this._render();

          // Let the cart drawer/notification render and open
          if (this.cartEl) {
            if (this.cartEl.classList.contains('is-empty')) {
              this.cartEl.classList.remove('is-empty');
            }
            this.cartEl.renderContents(response);
          }

          // Fire lightweight DOM event so other card-qty-toggle instances for
          // this same variant (if any exist on the page) can sync their state.
          // We do NOT publish to Shopify pubsub here because renderContents()
          // above already fully updates the cart drawer.
          document.dispatchEvent(
            new CustomEvent('cart:updated', {
              bubbles: true,
              detail: {
                cart: response,
                _source: 'card-qty-toggle-' + this.variantId,
              },
            })
          );
        } catch (err) {
          console.error('[card-qty-toggle] add error:', err);
        } finally {
          this._busy = false;
          this._setLoading(false);
        }
      }

      /**
       * Update an existing cart line quantity (increase or remove).
       */
      async _updateCart(newQty) {
        if (this._busy) return;
        this._busy = true;
        this._setLoading(true);

        try {
          const sections = this.cartEl
            ? this.cartEl.getSectionsToRender().map((s) => s.id)
            : [];

          const body = JSON.stringify({
            updates: { [this.variantId]: newQty },
            sections,
            sections_url: window.location.pathname,
          });

          const response = await fetch(`${routes.cart_update_url}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body,
          }).then((r) => r.json());

          // Update local state
          this._qty = newQty;
          this._render();

          // Sync cart drawer silently (no re-open)
          if (this.cartEl) this._updateCartUI(response);

          // Fire pubsub
          this._publishCartUpdate(response);
        } catch (err) {
          console.error('[card-qty-toggle] update error:', err);
        } finally {
          this._busy = false;
          this._setLoading(false);
        }
      }

      /**
       * Update the cart drawer / notification sections silently (no open).
       */
      _updateCartUI(parsedState) {
        if (!this.cartEl) return;
        const sectionsToRender = this.cartEl.getSectionsToRender?.() || [];
        sectionsToRender.forEach((section) => {
          const el = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!el || !parsedState.sections) return;
          const html = parsedState.sections[section.id];
          if (!html) return;
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const content = parsed.querySelector(section.selector || '.shopify-section');
          if (content) el.innerHTML = content.innerHTML;
        });

        // Show/hide empty state
        if (this.cartEl.classList) {
          this.cartEl.classList.toggle('is-empty', parsedState.item_count === 0);
        }
      }

      /**
       * Publish a cart update event for pubsub subscribers and dispatch a
       * DOM event for any external listeners.
       */
      _publishCartUpdate(cartData) {
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'card-qty-toggle',
            productVariantId: String(this.variantId),
            cartData,
          });
        }

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: {
              cart: cartData,
              _source: 'card-qty-toggle-' + this.variantId,
            },
          })
        );
      }

      // ─── Event handlers ───────────────────────────────────────────────────────

      _onAddToCart() {
        this._addToCart();
      }

      _onMinus() {
        const next = Math.max(0, this._qty - 1);
        this._updateCart(next);
      }

      _onPlus() {
        this._updateCart(this._qty + 1);
      }

      // ─── Render ───────────────────────────────────────────────────────────────

      _render() {
        if (this._qty > 0) {
          // Show quantity selector, hide add button
          this.classList.add('has-qty');
          if (this.qtyDisplay) this.qtyDisplay.textContent = this._qty;
        } else {
          // Show add button, hide quantity selector
          this.classList.remove('has-qty');
        }
      }

      _setLoading(on) {
        if (on) {
          this.addBtn?.setAttribute('disabled', '');
          this.addBtn?.classList.add('loading');
          this.addBtn?.querySelector('.loading__spinner')?.classList.remove('hidden');
          this.minusBtn?.setAttribute('disabled', '');
          this.plusBtn?.setAttribute('disabled', '');
          this.classList.add('is-loading');
        } else {
          this.addBtn?.removeAttribute('disabled');
          this.addBtn?.classList.remove('loading');
          this.addBtn?.querySelector('.loading__spinner')?.classList.add('hidden');
          this.minusBtn?.removeAttribute('disabled');
          this.plusBtn?.removeAttribute('disabled');
          this.classList.remove('is-loading');
        }
      }
    }
  );
}
